/**
 * "Confirme sua identidade": a segunda etapa do login numa estação, depois da
 * senha (protótipo `identityFaceModal`, MUDANCAS_WES_IDENTITY §1).
 *
 * Estados do protótipo: permission, starting, capture, recognizing,
 * camera-error, face-error, service-error, face-locked e pin — mais
 * account-error, para as recusas do backend que o protótipo não desenhou
 * (estação não registrada, operadora fora, muitas tentativas).
 *
 * O que é do backend e o que é desta tela, para ninguém confundir depois:
 *
 * - o rosto precisa ser da MESMA conta da senha. Não há rota que confirme
 *   "este rosto é do usuário que acabou de entrar"; o `/login/face` diz de
 *   quem é o rosto e abre sessão para essa pessoa. A tela confere o
 *   `user_id` e, se for outra pessoa, encerra tudo. É uma checagem da tela:
 *   o backend continua aceitando a senha sozinha em `/auth/login`;
 * - o limite de 3 tentativas de rosto é da tela (por e-mail, na sessão do
 *   navegador). O bloqueio do PIN é do backend, e a tela mostra o que ele diz;
 * - as três etapas do carregamento seguem o que acontece de fato: a captura
 *   termina quando há quadro, a conferência fica ativa enquanto a requisição
 *   está em voo, e o cadastro só fica pronto quando o backend aprovou.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import useFaceCapture, {
  CAPTURE_ERROR,
  CAPTURE_READY,
  ERROR_DENIED,
  ERROR_IN_USE,
  ERROR_LOST,
  ERROR_NO_CAMERA,
  ERROR_UNSUPPORTED,
} from '../../hooks/useFaceCapture';
import {
  loginFailureCode,
  loginWithFace,
  loginWithPin,
  refusalDetail,
} from '../../services/identityService';
import { IDENTITY_DEMO_ENABLED, firstNameOf } from '../../context/IdentitySessionContext';
import DemoFaceControls from './DemoFaceControls';

export const MAX_FACE_ATTEMPTS = 3;
const ATTEMPTS_KEY = 'hub360_identity_face_attempts';
const PIN_PATTERN = /^\d{4,8}$/;
const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'backspace', '0'];

// Motivos do PIN, com os códigos do protótipo.
export const PIN_REASONS = ['camera-defect', 'no-camera', 'face-not-recognized', 'ppe', 'service-down', 'other'];

const CAMERA_COPY = {
  [ERROR_DENIED]: 'blocked',
  [ERROR_NO_CAMERA]: 'notFound',
  [ERROR_IN_USE]: 'busy',
  [ERROR_LOST]: 'lost',
  [ERROR_UNSUPPORTED]: 'unsupported',
};

// Onde cada recusa para: [etapa que falha, estado da tela].
const FAILURE = {
  capture_failed: ['capture', 'camera-error'],
  not_recognized: ['match', 'face-error'],
  not_enrolled: ['match', 'face-error'],
  recognizer_unavailable: ['match', 'service-error'],
  too_many_requests: ['match', 'account-error'],
  station_not_registered: ['account', 'account-error'],
  credential_unavailable: ['account', 'account-error'],
};

// Contagem de falhas de rosto por e-mail: fechar o pop-up e digitar a senha
// de novo não zera; entrar zera.
function readAttempts(email) {
  try {
    const all = JSON.parse(window.sessionStorage.getItem(ATTEMPTS_KEY) || '{}');
    return all[email] || { failures: 0, last: null };
  } catch {
    return { failures: 0, last: null };
  }
}

function writeAttempts(email, value) {
  try {
    const all = JSON.parse(window.sessionStorage.getItem(ATTEMPTS_KEY) || '{}');
    if (value) all[email] = value;
    else delete all[email];
    window.sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(all));
  } catch {
    // Sem storage a contagem vale só enquanto o pop-up estiver aberto.
  }
}

const shake = keyframes`
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-3px); }
  40%, 60% { transform: translateX(3px); }
`;

const scan = keyframes`
  0% { top: 8%; }
  50% { top: 88%; }
  100% { top: 8%; }
`;

// Alerta vermelho com ícone e um leve tremor ao surgir; sem tremor para quem
// pede menos movimento no sistema.
function ErrorAlert({ icon, title, children }) {
  return (
    <Alert
      severity="error"
      icon={<span className="material-symbols-rounded" aria-hidden="true">{icon}</span>}
      sx={{
        borderRadius: 2,
        animation: `${shake} 0.4s ease-in-out`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Typography component="strong" sx={{ display: 'block', fontWeight: 700 }}>{title}</Typography>
      {children}
    </Alert>
  );
}

export default function IdentityConfirmDialog({
  open,
  email,
  displayName,
  expectedUserId,
  onConfirmed,
  onCancel,
  onDenied,
  onMismatch,
}) {
  const { t } = useTranslation();
  const camera = useFaceCapture();
  const [step, setStep] = useState('permission');
  const [steps, setSteps] = useState({ capture: null, match: null, account: null });
  const [confirmedTitle, setConfirmedTitle] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const [attempts, setAttempts] = useState(() => readAttempts(email));
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [pin, setPin] = useState('');
  const [pinFeedback, setPinFeedback] = useState(null);
  const [busy, setBusy] = useState(false);
  const lastFailure = useRef(null);
  const alive = useRef(true);

  const faceLocked = attempts.failures >= MAX_FACE_ATTEMPTS;
  const { start: startCamera, stop: stopCamera } = camera;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // Cada abertura começa do zero, a não ser pelo bloqueio do rosto, que é por
  // e-mail e sobrevive a fechar e abrir.
  useEffect(() => {
    if (!open) return;
    const current = readAttempts(email);
    setAttempts(current);
    setStep(current.failures >= MAX_FACE_ATTEMPTS ? 'face-locked' : 'permission');
    setSteps({ capture: null, match: null, account: null });
    setConfirmedTitle(false);
    setAccountError(null);
    setReason('');
    setReasonDetail('');
    setPin('');
    setPinFeedback(null);
    setBusy(false);
    lastFailure.current = null;
  }, [open, email]);

  // A câmera desliga ao ir para o PIN ou ao bloquear o rosto; o hook a desliga
  // ao fechar o pop-up.
  useEffect(() => {
    if (step === 'pin' || step === 'face-locked') stopCamera();
  }, [step, stopCamera]);

  useEffect(() => {
    if (camera.status === CAPTURE_READY && step === 'starting') setStep('capture');
    if (camera.status === CAPTURE_ERROR && step !== 'pin' && step !== 'face-locked') {
      lastFailure.current = 'camera-error';
      setStep('camera-error');
    }
  }, [camera.status, step]);

  const turnOnCamera = useCallback(() => {
    setStep('starting');
    startCamera();
  }, [startCamera]);

  const countFailure = (kind) => {
    const next = { failures: attempts.failures + 1, last: kind };
    writeAttempts(email, next);
    setAttempts(next);
    return next;
  };

  const markStep = (id, value) => setSteps((current) => ({ ...current, [id]: value }));

  const recognize = async () => {
    setStep('recognizing');
    setConfirmedTitle(false);
    setSteps({ capture: 'active', match: null, account: null });
    const blob = await camera.capture();
    if (!alive.current) return;
    if (!blob) {
      // A câmera falhou: não houve tentativa de reconhecimento, não conta.
      markStep('capture', 'failed');
      return; // o efeito da câmera leva para camera-error
    }
    markStep('capture', 'done');
    markStep('match', 'active');
    try {
      const session = await loginWithFace(blob);
      if (!alive.current) return;
      if (!expectedUserId || session.user_id !== expectedUserId) {
        // O rosto é de outra pessoa: a sessão que acabou de abrir não é de
        // quem digitou a senha. A página encerra as duas.
        //
        // Sem `expectedUserId` cai aqui também: não dá para provar que é a
        // mesma pessoa, e "não consigo provar" não pode valer como "confere".
        // Antes a comparação era pulada em silêncio (Bug #5145).
        markStep('match', 'failed');
        onMismatch();
        return;
      }
      markStep('match', 'done');
      markStep('account', 'done');
      setConfirmedTitle(true);
      writeAttempts(email, null);
      window.setTimeout(() => { if (alive.current) onConfirmed(session); }, 450);
    } catch (error) {
      if (!alive.current) return;
      const code = loginFailureCode(error);
      if (code === 'not_credentialed') {
        markStep('match', 'done');
        markStep('account', 'failed');
        onDenied(refusalDetail(error));
        return;
      }
      const [failedStep, nextState] = FAILURE[code] || ['match', 'service-error'];
      if (failedStep === 'account') markStep('match', 'done');
      markStep(failedStep, 'failed');
      window.setTimeout(() => {
        if (!alive.current) return;
        if (nextState === 'camera-error') {
          lastFailure.current = 'camera-error';
          setStep('camera-error');
          return;
        }
        if (nextState === 'account-error') {
          setAccountError(code);
          setStep('account-error');
          return;
        }
        lastFailure.current = nextState;
        const next = countFailure(nextState);
        setStep(next.failures >= MAX_FACE_ATTEMPTS ? 'face-locked' : nextState);
      }, 500);
    }
  };

  // Motivo sugerido a partir do problema que a pessoa acabou de ver.
  const suggestedReason = () => {
    if (step === 'face-locked') return attempts.last === 'service-error' ? 'service-down' : 'face-not-recognized';
    if (step === 'face-error') return 'face-not-recognized';
    if (step === 'service-error') return 'service-down';
    if (step === 'camera-error') return camera.errorKind === ERROR_NO_CAMERA ? 'no-camera' : 'camera-defect';
    return '';
  };

  const goToPin = () => {
    const suggestion = suggestedReason();
    if (suggestion && !reason) setReason(suggestion);
    setPinFeedback(null);
    setStep('pin');
  };

  const submitPin = async () => {
    if (!reason) {
      setPinFeedback(t('identity.confirm.pin.errors.reasonRequired'));
      return;
    }
    if (reason === 'other' && reasonDetail.trim().length < 5) {
      setPinFeedback(t('identity.confirm.pin.errors.detailRequired'));
      return;
    }
    if (!PIN_PATTERN.test(pin)) {
      setPinFeedback(t('identity.login.pinInvalid'));
      return;
    }
    setBusy(true);
    setPinFeedback(null);
    try {
      const session = await loginWithPin(email, pin, {
        code: reason,
        detail: reason === 'other' ? reasonDetail.trim() : null,
      });
      if (!alive.current) return;
      writeAttempts(email, null);
      onConfirmed(session);
    } catch (error) {
      if (!alive.current) return;
      const code = loginFailureCode(error, { viaPin: true });
      setPin('');
      if (code === 'not_credentialed') {
        onDenied(refusalDetail(error));
        return;
      }
      const key = {
        pin_refused: 'identity.confirm.pin.errors.refused',
        pin_locked: 'identity.login.pinLocked',
        too_many_requests: 'identity.confirm.errors.tooMany',
        station_not_registered: 'identity.login.states.station-error.body',
        credential_unavailable: 'identity.login.states.credential-error.body',
      }[code] || 'identity.login.unavailable';
      setPinFeedback(t(key));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const pressKey = (key) => {
    setPin((value) => (key === 'backspace' ? value.slice(0, -1) : `${value}${key}`.slice(0, 8)));
  };

  const primary = {
    permission: { label: t('identity.confirm.actions.enableCamera'), onClick: turnOnCamera },
    starting: { label: t('identity.confirm.actions.wait'), onClick: () => {}, busy: true },
    capture: { label: t('identity.confirm.actions.recognize'), onClick: recognize },
    recognizing: { label: t('identity.confirm.actions.recognizing'), onClick: () => {}, busy: true },
    'camera-error': { label: t('identity.login.tryAgain'), onClick: turnOnCamera },
    'face-error': { label: t('identity.login.tryAgain'), onClick: camera.isReady ? () => setStep('capture') : turnOnCamera },
    'service-error': { label: t('identity.login.tryAgain'), onClick: camera.isReady ? () => setStep('capture') : turnOnCamera },
    'account-error': accountError === 'station_not_registered'
      ? { label: t('identity.confirm.actions.backToLogin'), onClick: onCancel }
      : { label: t('identity.login.tryAgain'), onClick: camera.isReady ? () => setStep('capture') : turnOnCamera },
    'face-locked': { label: t('identity.login.signInWithPin'), onClick: goToPin },
    pin: { label: t('identity.login.signInWithPin'), onClick: submitPin, busy },
  }[step];

  const isPin = step === 'pin';
  const hideCamera = isPin || step === 'face-locked';
  const firstName = firstNameOf(displayName);
  const left = MAX_FACE_ATTEMPTS - attempts.failures;
  const cameraCopy = CAMERA_COPY[camera.errorKind] || 'blocked';

  const stepLabel = (id) => t(`identity.confirm.steps.${id}`);
  const stepIcon = (value) => {
    if (value === 'done') return <span className="material-symbols-rounded" aria-hidden="true">check_circle</span>;
    if (value === 'failed') return <span className="material-symbols-rounded" aria-hidden="true">cancel</span>;
    if (value === 'active') return <CircularProgress size={16} />;
    return <span className="material-symbols-rounded" aria-hidden="true">radio_button_unchecked</span>;
  };

  return (
    <Dialog
      open={open}
      onClose={(event, why) => { if (why !== 'backdropClick') onCancel(); }}
      fullWidth
      maxWidth="xs"
      aria-labelledby="identityFaceTitle"
      aria-describedby="identityFaceSubtitle"
      PaperProps={{ id: 'identityFaceModal', sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, pb: 1 }}>
        <Box>
          <Typography id="identityFaceTitle" component="h3" variant="h6" sx={{ fontWeight: 700 }}>
            {t('identity.confirm.title')}
          </Typography>
          <Typography id="identityFaceSubtitle" variant="body2" color="text.secondary">
            {firstName
              ? t('identity.confirm.subtitleNamed', { name: firstName })
              : t('identity.confirm.subtitle')}
          </Typography>
        </Box>
        <IconButton aria-label={t('identity.confirm.close')} onClick={onCancel} data-identity-face-close>
          <span className="material-symbols-rounded" aria-hidden="true">close</span>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        {!hideCamera ? (
          <Box
            id="identityFaceCamera"
            sx={(theme) => ({
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: '4 / 3',
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
            })}
          >
            <Box
              component="video"
              ref={camera.videoRef}
              muted
              playsInline
              autoPlay
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
                visibility: camera.isReady ? 'visible' : 'hidden',
              }}
            />
            <Box
              aria-hidden="true"
              sx={(theme) => ({
                position: 'relative',
                width: '48%',
                aspectRatio: '3 / 4',
                borderRadius: '45%',
                border: `3px dashed ${theme.palette.primary.main}`,
              })}
            />
            {step === 'recognizing' ? (
              <Box
                aria-hidden="true"
                className="identity-face-scan-line"
                sx={(theme) => ({
                  position: 'absolute',
                  left: '8%',
                  right: '8%',
                  height: 3,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.6)}`,
                  animation: `${scan} 1.6s ease-in-out infinite`,
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none', top: '50%' },
                })}
              />
            ) : null}
            {!camera.isReady ? (
              <Box component="span" className="material-symbols-rounded" aria-hidden="true" sx={{ position: 'absolute', fontSize: 44 }}>
                {step === 'camera-error' ? 'videocam_off' : 'videocam'}
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Box data-identity-face-state={step} sx={{ display: 'grid', gap: 1 }}>
          {step === 'permission' ? (
            <>
              <Typography component="strong" sx={{ fontWeight: 700 }}>{t('identity.confirm.states.permission.title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('identity.confirm.states.permission.body')}</Typography>
            </>
          ) : null}
          {step === 'starting' ? (
            <>
              <Typography component="strong" sx={{ fontWeight: 700 }}>{t('identity.confirm.states.starting.title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('identity.confirm.states.starting.body')}</Typography>
            </>
          ) : null}
          {step === 'capture' ? (
            <>
              <Typography component="strong" sx={{ fontWeight: 700 }}>{t('identity.confirm.states.capture.title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('identity.confirm.states.capture.body')}</Typography>
            </>
          ) : null}
          {step === 'recognizing' ? (
            <Box role="status" aria-live="polite" sx={{ display: 'grid', gap: 1 }}>
              <Typography component="strong" sx={{ fontWeight: 700 }}>
                {t(confirmedTitle ? 'identity.confirm.states.recognizing.done' : 'identity.confirm.states.recognizing.title')}
              </Typography>
              <Box component="ol" id="identityFaceSteps" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75 }}>
                {['capture', 'match', 'account'].map((id) => (
                  <Box
                    component="li"
                    key={id}
                    data-face-step={id}
                    data-state={steps[id] || 'pending'}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.9rem',
                      color: steps[id] === 'failed'
                        ? 'error.main'
                        : steps[id] === 'done'
                          ? 'success.main'
                          : steps[id] === 'active' ? 'text.primary' : 'text.secondary',
                      '& .material-symbols-rounded': { fontSize: 18 },
                    }}
                  >
                    {stepIcon(steps[id])}
                    <span>{stepLabel(id)}</span>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
          {step === 'camera-error' ? (
            <ErrorAlert icon="videocam_off" title={t('identity.confirm.states.cameraError.title')}>
              {t(`identity.confirm.cameraErrors.${cameraCopy}`)}
            </ErrorAlert>
          ) : null}
          {step === 'face-error' ? (
            <ErrorAlert icon="no_accounts" title={t('identity.confirm.states.faceError.title')}>
              {t('identity.confirm.states.faceError.body')}
            </ErrorAlert>
          ) : null}
          {step === 'service-error' ? (
            <ErrorAlert icon="cloud_off" title={t('identity.confirm.states.serviceError.title')}>
              {t('identity.confirm.states.serviceError.body')}
            </ErrorAlert>
          ) : null}
          {step === 'account-error' ? (
            <ErrorAlert icon="error" title={t(`identity.confirm.accountErrors.${accountError || 'generic'}.title`)}>
              {t(`identity.confirm.accountErrors.${accountError || 'generic'}.body`)}
            </ErrorAlert>
          ) : null}
          {step === 'face-locked' ? (
            <ErrorAlert icon="lock" title={t('identity.confirm.states.faceLocked.title')}>
              {t('identity.confirm.states.faceLocked.body', { count: MAX_FACE_ATTEMPTS })}
            </ErrorAlert>
          ) : null}
          {(step === 'face-error' || step === 'service-error') && left > 0 ? (
            <Typography id="identityFaceAttempts" variant="body2" color="error.main" role="status">
              {t('identity.confirm.attemptsLeft', { count: left })}
            </Typography>
          ) : null}

          {isPin ? (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Typography component="strong" sx={{ fontWeight: 700 }}>{t('identity.confirm.pin.title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('identity.confirm.pin.body')}</Typography>
              <TextField
                select
                size="small"
                id="identityPinReason"
                label={t('identity.confirm.pin.reason')}
                required
                value={reason}
                onChange={(event) => { setReason(event.target.value); setPinFeedback(null); }}
                disabled={busy}
              >
                {PIN_REASONS.map((code) => (
                  <MenuItem key={code} value={code}>{t(`identity.confirm.pin.reasons.${code}`)}</MenuItem>
                ))}
              </TextField>
              {reason === 'other' ? (
                <TextField
                  id="identityPinReasonOther"
                  label={t('identity.confirm.pin.detail')}
                  placeholder={t('identity.confirm.pin.detailPlaceholder')}
                  required
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 200 }}
                  value={reasonDetail}
                  onChange={(event) => setReasonDetail(event.target.value)}
                  disabled={busy}
                />
              ) : null}
              <TextField
                id="identityLoginPinInput"
                type="password"
                label={t('identity.login.pinLabel')}
                required
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitPin();
                  }
                }}
                placeholder={t('identity.login.pinPlaceholder')}
                inputProps={{ inputMode: 'numeric', maxLength: 8, autoComplete: 'off' }}
                disabled={busy}
              />
              <Box
                role="group"
                aria-label={t('identity.login.keypad')}
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gap: 1, justifyContent: 'center' }}
              >
                {KEYPAD.map((key) => (
                  <Button
                    key={key}
                    variant="outlined"
                    onClick={() => pressKey(key)}
                    disabled={busy}
                    aria-label={key === 'backspace' ? t('identity.login.backspace') : undefined}
                    sx={{ minWidth: 0, minHeight: 44, fontSize: '1.05rem', fontWeight: 600, borderRadius: 2 }}
                    data-identity-login-key={key}
                  >
                    {key === 'backspace' ? (
                      <span className="material-symbols-rounded" aria-hidden="true">backspace</span>
                    ) : key}
                  </Button>
                ))}
              </Box>
              {pinFeedback ? (
                <ErrorAlert icon="error" title={pinFeedback} />
              ) : null}
            </Box>
          ) : null}
        </Box>

        {/* No PIN: "Tentar novamente com o rosto" à esquerda e "Entrar com PIN"
          * à direita, em partes iguais; no celular empilham, PIN em cima. Sem
          * volta para a câmera quando o rosto está bloqueado. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1.5,
          }}
        >
          {isPin && !faceLocked ? (
            <Button
              variant="outlined"
              id="identityFaceSecondary"
              onClick={turnOnCamera}
              disabled={busy}
              sx={{ flex: 1, py: 1.25, borderRadius: 2, fontWeight: 600 }}
            >
              {t('identity.confirm.actions.retryFace')}
            </Button>
          ) : null}
          <Button
            variant="contained"
            id="identityFacePrimary"
            onClick={primary.onClick}
            disabled={Boolean(primary.busy) || (step === 'capture' && !camera.isReady)}
            startIcon={primary.busy ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : null}
            sx={{ flex: 1, py: 1.25, borderRadius: 2, fontWeight: 600 }}
          >
            {primary.label}
          </Button>
        </Box>

        {!hideCamera ? (
          <Button
            variant="text"
            id="identityFacePinLink"
            onClick={goToPin}
            disabled={step === 'recognizing'}
            startIcon={<span className="material-symbols-rounded" aria-hidden="true">build</span>}
            sx={{ justifySelf: 'center', fontWeight: 600 }}
          >
            {t('identity.confirm.actions.pinLink')}
          </Button>
        ) : null}

        {IDENTITY_DEMO_ENABLED && !hideCamera ? <DemoFaceControls /> : null}
      </DialogContent>
    </Dialog>
  );
}
